import { useCurrentStateAndParams } from "@uirouter/react";
import { useSelector } from "react-redux";

import { colorsPalettes } from "@similarweb/styles";
import { Tab, TabList, TabPanel, Tabs } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { GaErrorBanner } from "app/modules/ga-connect";
import { GaMappingWarningBanner } from "app/pages/activation";
import { useTranslation } from "pro-features/components/WithTranslation";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { ChannelsTab } from "../../channels";
import { TAB_DICTIONARY, type BenchmarkTabName } from "../../config";
import { generateAutomationName } from "../../helpers/helpers";
import useMetricsRequest from "../../hooks/useMetricsRequest";
import { benchmarkTrackerTracking } from "../../services/tracking";
import { selectGaErrorKind } from "../../store/selectors/reportSelectors";
import type { BenchmarkUrlParams } from "../../types/paramsTypes";

import TabContent from "./Tab/Tab";

const allTabs = Object.keys(TAB_DICTIONARY) as BenchmarkTabName[];

interface IReportContentProps {
    activationId?: string;
    isGaSource?: boolean;
}

export const ReportContent = ({ activationId, isGaSource }: IReportContentProps) => {
    const translate = useTranslation();

    const {
        params: { tab },
    } = useCurrentStateAndParams<BenchmarkUrlParams>();
    const tabIndex = Number(tab);

    const { refetch } = useMetricsRequest(tabIndex);

    const gaErrorKind = useSelector(selectGaErrorKind);

    const onSelect = (index: number) => {
        benchmarkTrackerTracking.report.trackTabSelection(allTabs[index]);

        void proNavigator.updateParams({ tab: index });
    };

    return (
        <Tabs
            selectedIndex={tabIndex}
            onSelect={onSelect}
        >
            <Stack gap="32px">
                <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    background={colorsPalettes.bluegrey[100]}
                    data-automation={generateAutomationName("report", ["tabs", "headers"])}
                >
                    <TabList>
                        {allTabs.map((tab) => (
                            <Tab key={tab}>
                                <Text fontVariant="tab">{translate(TAB_DICTIONARY[tab].title)}</Text>
                            </Tab>
                        ))}
                    </TabList>
                </Flex>

                {gaErrorKind && isGaSource ? (
                    <GaErrorBanner
                        errorKind={gaErrorKind}
                        onReload={refetch}
                    />
                ) : null}

                {activationId ? (
                    <GaMappingWarningBanner
                        activationId={activationId}
                        dataAutomationPrefix={generateAutomationName("report", ["ga", "info-banner"])}
                        isGaSource={isGaSource}
                    />
                ) : null}

                <div data-automation={generateAutomationName("report", ["metric", "wrapper"])}>
                    <TabPanel>
                        <TabContent tabName="overview" />
                    </TabPanel>
                    <TabPanel>
                        <TabContent tabName="engagement" />
                    </TabPanel>
                    <TabPanel>
                        <ChannelsTab />
                    </TabPanel>
                </div>
            </Stack>
        </Tabs>
    );
};
